import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContentBaseLayout from '../../../libx/shared/ContentBaseLayout';
import ProductList from '../../../../shared/common/ProductList';
import { FilterBar } from '../../../../shared/common/FilterBar';
import { LOCAL_COLLECTION_SORT_BY } from '../../../../shared/common/SortByDropDown';
import Pagination, { getPaginationStateObject } from '../../../../shared/common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../../shared/components/NoElementsFound';
import ListItem from '../../../../shared/common/ListItems/ListItem';
import { ProductGroupTile } from '../ProductGroupTile';
import { globalError } from '../../../../store/notifications/actions';
import { setActiveList } from '../../../../store/orderlist/actions';
import Headline from './Headline';
import { DISPLAY_TYPE_TILE } from '../../../../store/displayType/types';
import { CopyDetailsPopup } from '../../../collection/copyDetailsPopup/CopyDetailsPopup';
import { useHistory, useLocation, useParams } from 'react-router';
import { AppState } from '../../../../store/appState';
import { useQueryParams } from '../../../../shared/hooks/useQueryParams';
import {
    invalidateLibraryManagedProductGroupsFeaturePages,
    useLibraryManagedProductGroups,
    useLibraryManagedProductGroupsFeaturePages
} from '../api/useLibraryManagedProductGroupsFeaturePages';
import queryString from 'query-string';
import { deserializeFilter } from '../../../../shared/utils/deserializeFilter';
import { useUpdateProductGroup } from '../../../../api/library-managed/useUpdateProductGroup';
import { useUpdateProductGroupSlot } from '../../../../api/library-managed/useUpdateProductGroupSlot';
import { DisplayTypeState } from '../../../../store/displayType/state';
import { modifyURLState, modifyURLStates } from '../../../../shared/utils/utils-routing';
import { useDeleteProductFromProductGroup } from '../../../../api/library-managed/useDeleteProductFromProductGroup';
import { useDeleteAllProductsFromProductGroup } from '../../../../api/library-managed/useDeleteAllProductsFromProductGroup';
import { ProductMetaData, ProductSorting } from '../../../../../models/Product';
import { DEFAULT_PAGE_SIZE } from '../../../collection/by-genre/CollectionByGenrePage';
import {
    invalidateLibraryManagedProductGroupProducts,
    useLibraryManagedProductGroupProducts
} from '../../../../api/library-managed/useLibraryManagedProductGroupProducts';
import { Orderlist } from '../../../../../models/OrderList';
import { ResponsiveFilterBar } from '../../../../shared/filter/ResponsiveFilterBar';
import { fetchProductGroupDetailsAggregationsQuickSearch } from '../../../../api/library-managed/product-group-detail/fetchProductGroupDetailsAggregationsQuicksearch';
import { QueryKeyBuilder } from '../../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../../api/listContent/fetchAggregations';
import { fetchProductGroupDetailsAggregations } from '../../../../api/library-managed/product-group-detail/fetchProductGroupDetailsAggregations';
import { FetchAggregationsQuickSearch } from '../../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../../shared/filter/defaultVisibleFilterGroups';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Article, SubscriptionPeriodType } from '../../../../../models/Article';
import { useAddBulkFromProductGroupToSelectionList } from '../../../../api/selection-list/useAddFromProductGroupToList';
import { useAddBulkFromProductGroupToCart } from '../../../../api/shopping-cart/useAddFromProductGroupToCart';
import { useAddToList } from '../../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../../shared/common/CheckBeforeAddingModal';
import { useAddToGroupBulk } from '../../../../api/productGroups/useAddToGroupBulk';
import { useAddProductToGroup } from '../../../../api/productGroups/useAddProductToGroup';
import { useRemoveFromGroupBulk } from '../../../../api/productGroups/useRemoveFromGroupBulk';
import { PriceTier } from '../../../../../models/PriceTier';
import { useProductSelection } from '../../../../shared/product-selection/useProductSelection';
import { hasProductSelection, nothingSelected } from '../../../../shared/product-selection/ProductSelectionUtils';
import { UnavailableProductsModal } from '../../../../shared/common/UnavailableProductsModal';

const DOCUMENT_TITLE = 'Product Group';

export const ProductGroupDetail: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { search } = useLocation();

    const authentication = useSelector((state: AppState) => state.authentication);
    const activeList = useSelector((state: AppState) => state.orderlists.active);
    const orderlists = useSelector((state: AppState) => state.orderlists.list);

    const { onSelectButtonClick, productSelection, cancelSelection, selectAll, toggleSelectionMode } =
        useProductSelection();

    const [product, setProduct] = useState<(ProductMetaData & Article) | undefined>(undefined);

    const { productGroupId } = useParams<{ productGroupId: string }>();

    const { data: featuredPages, isLoading: isLoadingFeaturePages } = useLibraryManagedProductGroupsFeaturePages();
    const featuredPage = featuredPages?.find(page =>
        page?.productGroupSlots?.find(slot => slot?.productGroup?.id === parseInt(productGroupId))
    );
    const productGroupSlot = featuredPage?.productGroupSlots?.find(
        slot => slot?.productGroup?.id === parseInt(productGroupId)
    );
    const productGroup = productGroupSlot?.productGroup;

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },

        filter: { value: filterQueryParam },
        display: { value: display }
    } = useQueryParams({
        page: 'integer',
        sortBy: 'string',
        pageSize: 'integer',

        filter: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy =
        sortByParam ?? (productGroup?.sorting === 'custom' ? 'custom' : ProductSorting.RELEASE_DATE_NEW_TO_OLD);
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);

    const { data: products, isLoading: isLoadingProducts } = useLibraryManagedProductGroupProducts({
        productGroupId: productGroupId,
        pagination: { sortBy, page, pageSize },
        filterMap: filter,
        enabled: !isLoadingFeaturePages && !!productGroup
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(() => defaultVisibleFilterGroups(authentication), [authentication]);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'productGroupDetailPage',
            productGroupId,
            'aggregation',
            filters,
            expandedFilterGroups
        ],
        [productGroupId]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchProductGroupDetailsAggregations({ ...options, productGroupId }),
        [productGroupId]
    );
    const fetchAggregationsQuickSeach = useCallback<FetchAggregationsQuickSearch>(
        options => fetchProductGroupDetailsAggregationsQuickSearch({ ...options, productGroupId }),
        [productGroupId]
    );
    //endregion

    const libraryManagedProductGroups = useLibraryManagedProductGroups();
    const { mutate: editProductGroup, isLoading: isLoadingEditProductGroup } = useUpdateProductGroup();
    const { mutate: toggleActiveState, isLoading: isLoadingToggleProductGroupSlot } = useUpdateProductGroupSlot();
    const { mutate: deleteProductFromProductGroup, isLoading: isLoadingDeleteProductFromProductGroup } =
        useDeleteProductFromProductGroup(productGroupId);
    const { mutate: deleteAllProducts, isLoading: isDeleteProductsLoading } =
        useDeleteAllProductsFromProductGroup(productGroupId);

    function handleSaveProductGroupTitle(title) {
        if (!productGroup) {
            dispatch(globalError(t('marketing.productGroups.warnings.saveGroupTitle')));
            return;
        }

        editProductGroup({
            productGroupId: productGroup.id,
            body: { title, sorting: productGroup.sorting }
        });
    }

    function saveProductGroupActiveState(active) {
        if (!productGroupSlot) {
            dispatch(globalError(t('marketing.productGroups.warnings.saveGroupActivationState')));
            return;
        }

        toggleActiveState({ slotId: productGroupSlot.id, body: { active } });
    }

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: 'sortBy', value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            'page',
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({
        callback: () => invalidateLibraryManagedProductGroupProducts(productGroupId)
    });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const removeFromGroupBulk = useRemoveFromGroupBulk({
        productGroupId,
        filterQueryParam: filterQueryParam ?? '',
        onSuccess: () => {
            invalidateLibraryManagedProductGroupsFeaturePages().then();
            invalidateLibraryManagedProductGroupProducts(productGroupId).then();
            cancelSelection();
        },
        productSelection
    });

    const { isLoading: isLoadingAddToList, addToListBulk: addBulkProductsToList } =
        useAddBulkFromProductGroupToSelectionList({
            filter: filterQueryParam ?? '',
            productGroupId,
            onSuccess: () => {
                cancelSelection();
            },
            productSelection
        });

    const { isLoading: isLoadingAddToCart, addToCartBulk: addBulkProductsToCart } = useAddBulkFromProductGroupToCart({
        filter: filterQueryParam ?? '',
        productGroupId,
        onSuccess: () => {
            invalidateLibraryManagedProductGroupProducts(productGroupId).then();
            cancelSelection();
        },
        productSelection
    });

    const addToGroupBulk = useAddToGroupBulk({
        sourceProductGroupId: productGroupId,
        filterQueryParam: filterQueryParam ?? '',
        productSelection,
        cancelSelection
    });
    const addProductToGroup = useAddProductToGroup();

    const onBulkAddToList = (
        list: Orderlist,
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => {
        return addBulkProductsToList(
            list.id,
            performChecks,
            priceTier,
            subscriptionPeriodType
        ).then(data => {
            if (!performChecks) {
                dispatch(setActiveList(list));
            }
            return data;
        });
    };

    const excludeSorting = productGroup?.sorting !== 'custom' ? ['custom'] : [];
    const productsFound = (products?.totalHits ?? 0) > 0;

    const totalProducts = products?.totalHits ?? 0;

    const loading =
        isLoadingFeaturePages ||
        isLoadingProducts ||
        isLoadingEditProductGroup ||
        isLoadingToggleProductGroupSlot ||
        isLoadingDeleteProductFromProductGroup ||
        isDeleteProductsLoading ||
        isLoadingAddToList ||
        isLoadingAddToCart;

    return (
        <ContentBaseLayout
            loading={loading}
            documentTitle={DOCUMENT_TITLE}
            contentComponent={
                <>
                    <Headline
                        title={productGroup?.title ?? ''}
                        isAddToListEnabled={true}
                        active={productGroupSlot?.active ?? false}
                        sorting={productGroup?.sorting!}
                        onSaveTitle={handleSaveProductGroupTitle}
                        onRemoveFromProductGroup={removeFromGroupBulk}
                        onRemoveAllProducts={deleteAllProducts}
                        productSelection={productSelection}
                        isInSelectionMode={hasProductSelection(productSelection)}
                        cancelSelectionMode={cancelSelection}
                        usageStatistics={productGroupSlot?.usageStatistics}
                        onToggleActive={saveProductGroupActiveState}
                        productAmount={products?.totalHits ?? 0}
                        productGroups={libraryManagedProductGroups}
                        onBulkAddToGroup={addToGroupBulk}
                        onBulkAddToCart={addBulkProductsToCart}
                        onAddToList={onBulkAddToList}
                        isNothingSelected={nothingSelected(productSelection)}
                    />
                    {!productsFound ? null : (
                        <FilterBar
                            dispatch={dispatch}
                            totalProducts={totalProducts}
                            sortBy={sortBy}
                            currentPage={page}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            displayType={display as DisplayTypeState}
                            onFilterDisplayToggle={onFilterDisplayToggle}
                            sorting={LOCAL_COLLECTION_SORT_BY}
                            excludeSorting={excludeSorting}
                            hideGroupBy={true}
                            hideSelectButtons={false}
                            hideSelectAll={false}
                            onSortChange={setSortBy}
                            onDisplayChange={onDisplayChange}
                            toggleSelectionMode={toggleSelectionMode}
                            productSelection={productSelection}
                            selectAll={() => selectAll(totalProducts)}
                            displayFilter={displayFilter}
                        />
                    )}
                    <div className="content-area-inner flex-ca">
                        {displayFilter ? (
                            <ResponsiveFilterBar
                                visibleFilterGroups={visibleFilterGroups}
                                buildQueryKey={aggregationsQueryKeyBuilder}
                                fetchAggregations={fetchAggregations}
                                fetchAggregationsQuickSearch={fetchAggregationsQuickSeach}
                            />
                        ) : null}
                        {!productsFound && !loading ? (
                            <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                        ) : (
                            <div className={classNames({ 'min-h-screen': loading }, 'ca-main')}>
                                {!productsFound ? null : (
                                    <div className="ca-main-inner">
                                        <ProductList
                                            onAddToGroup={addProductToGroup}
                                            orderlists={orderlists}
                                            activeList={activeList}
                                            displayType={display || DISPLAY_TYPE_TILE}
                                            tileItemComponent={ProductGroupTile}
                                            showManagePopup={(product: ProductMetaData & Article) =>
                                                setProduct(product)
                                            }
                                            listItemComponent={ListItem}
                                            productGroups={libraryManagedProductGroups}
                                            onSelectProduct={onSelectButtonClick}
                                            onRemoveProductFromList={deleteProductFromProductGroup}
                                            showRemoveButton={true}
                                            showSelectButton={true}
                                            productSelection={productSelection}
                                            products={products?.hits ?? []}
                                            currentPage={page}
                                            onAddPrice={changePrice}
                                            isProductSelectable={true}
                                        />
                                        <Pagination
                                            totalItems={totalProducts}
                                            onPageChange={setPage}
                                            currentPage={page}
                                            pageSize={pageSize}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {product ? (
                        <CopyDetailsPopup
                            onClosePopup={() => setProduct(undefined)}
                            onAddPrice={changePrice}
                            product={product}
                            shopLanguage={undefined}
                        />
                    ) : null}
                    <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
                    <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
                </>
            }
        />
    );
};
